import { createQueryKeys } from "@lukemorales/query-key-factory";

import type { CoreApiClient } from "../client";
import type { AirHandlerUnit, FullAirHandlerUnit } from "../types/air-handler-unit.type";
import type { AirHandlerUnitPlannings } from "../types/planning.type";

export const createAirHandlerUnitQueries = (api: CoreApiClient) => {
  const { client } = api;

  const list = async (buildingId: string): Promise<AirHandlerUnit[]> => {
    const { data, error } = await client.GET(`/buildings/{buildingId}/air-handler-units`, {
      params: { path: { buildingId } },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  const byId = async (airHandlerUnitId: string): Promise<FullAirHandlerUnit> => {
    const { data, error } = await client.GET(`/air-handler-units/{airHandlerUnitId}`, {
      params: { path: { airHandlerUnitId } },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  /**
   * Live/historical snapshot. No `date` → the backend round-trips the gateway (poll this in Live mode); a
   * `date` (ISO) → the state in effect at that instant, read from the timeseries.
   */
  const state = async (airHandlerUnitId: string, date?: string): Promise<FullAirHandlerUnit> => {
    const { data, error } = await client.GET(`/air-handler-units/{airHandlerUnitId}/state`, {
      params: { path: { airHandlerUnitId }, query: date ? { date } : undefined },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  /** Distinct instants (newest first) with a historical sample — the history slider's real data points. */
  const stateDates = async (airHandlerUnitId: string, startDate: string, endDate: string): Promise<string[]> => {
    const { data, error } = await client.GET(`/air-handler-units/{airHandlerUnitId}/state/dates`, {
      params: { path: { airHandlerUnitId }, query: { startDate, endDate } },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  /**
   * Weekly plannings (user-defined + gateway computed) and the dated exceptional periods persisted
   * server-side.
   */
  const plannings = async (airHandlerUnitId: string): Promise<AirHandlerUnitPlannings> => {
    const { data, error } = await client.GET(`/air-handler-units/{airHandlerUnitId}/plannings`, {
      params: { path: { airHandlerUnitId } },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  return {
    list,
    byId,
    state,
    stateDates,
    plannings,
    queries: createQueryKeys("airHandlerUnits", {
      building: (buildingId: string) => ({
        queryKey: [buildingId],
        queryFn: () => list(buildingId),
      }),
      airHandlerUnit: (airHandlerUnitId: string) => ({
        queryKey: [airHandlerUnitId],
        queryFn: () => byId(airHandlerUnitId),
      }),
      state: (airHandlerUnitId: string, date?: string) => ({
        queryKey: [airHandlerUnitId, "state", date ?? "live"],
        queryFn: () => state(airHandlerUnitId, date),
      }),
      stateDates: (airHandlerUnitId: string, startDate: string, endDate: string) => ({
        queryKey: [airHandlerUnitId, "state-dates", startDate, endDate],
        queryFn: () => stateDates(airHandlerUnitId, startDate, endDate),
      }),
      plannings: (airHandlerUnitId: string) => ({
        queryKey: [airHandlerUnitId, "planning"],
        queryFn: () => plannings(airHandlerUnitId),
      }),
    }),
  };
};
